import { useQuery } from "@apollo/client";
import { Alert, Drawer, Empty, Pagination, Skeleton } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { GET_LEAD_AUDIT_LOG } from "../../../../graphql/query";
import useLang from "../../../../hooks/useLang";
import { getMobileView } from "../../../../redux/slices/isMobile";
import ChipCheckboxGroup from "../../../commonComponents/ChipCheckboxGroup";
import AuditLogItem from "./AuditLogItem";
import { AUDIT_LOG_FILTERS, AUDIT_LOG_PAGE_SIZE } from "./constants";
import mapAuditLogEntry from "./mapAuditLogEntry";

/**
 * Consent Audit log — a read-only history of the consent changes on a lead,
 * opened from the "Consents" section of the lead profile.
 *
 * Answers the questions the Consents panel cannot: who changed a consent, when,
 * and how (manual vs. web form vs. email reply).
 *
 * Backed by `getLeadAuditLog`, filtered server-side by the chip selection and
 * paged server-side. Rows are shaped for display by `mapAuditLogEntry`.
 *
 * Layout follows Figma node 27023-48954: 24px body padding, 24px between the
 * filter chips and the list, 20px between rows. Wider than the 600px in the
 * frame — see DRAWER_WIDTH.
 */

/**
 * The design's 600px predates the fifth chip (Otto campaign). All six chips
 * measure ~606px, so at 600px minus 48px of body padding the row wrapped onto
 * two lines. 720px keeps them on one line with headroom for longer labels; the
 * chip group still wraps if a translation outgrows it.
 */
const DRAWER_WIDTH = 720;
const ConsentAuditLogDrawer = ({ open, onClose, leadId, leadName }) => {
  const [lang] = useLang();
  const isMobileView = useSelector(state => getMobileView(state));
  // View-local: neither the chip selection nor the page is worth persisting.
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [page, setPage] = useState(1);

  // An empty selection is "All", which means no `category` variable at all.
  const activeCategory = selectedCategories[0] || null;

  // Changing the filter re-pages from the start, otherwise page 3 of "All" can
  // land past the end of a narrower category and show an empty drawer.
  useEffect(() => setPage(1), [activeCategory]);
  // Reopening on a different lead must not inherit the previous lead's page.
  useEffect(() => {
    if (open) setPage(1);
  }, [open, leadId]);

  const { data, loading, error } = useQuery(GET_LEAD_AUDIT_LOG, {
    variables: {
      leadId: Number(leadId),
      ...(activeCategory ? { category: activeCategory } : {}),
      page,
      pageSize: AUDIT_LOG_PAGE_SIZE,
    },
    // Nothing to fetch until the drawer is actually open on a lead.
    skip: !open || !leadId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const filterOptions = useMemo(
    () => AUDIT_LOG_FILTERS.map(({ langKey, value }) => ({ label: lang[langKey], value })),
    [lang]
  );

  const total = data?.getLeadAuditLog?.count ?? 0;
  const items = useMemo(
    () =>
      (data?.getLeadAuditLog?.data || []).map(entry =>
        mapAuditLogEntry(entry, activeCategory, leadName)
      ),
    [data, activeCategory, leadName]
  );

  // `cache-and-network` re-renders with the previous page still in `data`, so
  // only blank the list out when there is genuinely nothing to show yet.
  const showSkeleton = loading && !items.length;

  const renderBody = () => {
    if (showSkeleton) return <Skeleton active paragraph={{ rows: 6 }} title={false} />;
    if (error) {
      return <Alert type="error" showIcon message={error.message || lang.somethingWentWrong} />;
    }
    if (!items.length) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={lang.auditLogEmpty} />;
    }
    return (
      <div className="flex flex-col gap-5">
        {items.map(item => (
          <AuditLogItem key={item.id} item={item} />
        ))}
      </div>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className="normal-popup"
      width={isMobileView ? "100%" : DRAWER_WIDTH}
      title={lang.auditLog}
      styles={{ body: { padding: 24 } }}
      destroyOnClose
    >
      <div className="flex flex-col gap-6">
        <ChipCheckboxGroup
          appearance="pill"
          multiple={false}
          allLabel={lang.all}
          options={filterOptions}
          value={selectedCategories}
          onChange={setSelectedCategories}
        />
        {renderBody()}
        {total > AUDIT_LOG_PAGE_SIZE && (
          <Pagination
            size="small"
            align="center"
            current={page}
            total={total}
            pageSize={AUDIT_LOG_PAGE_SIZE}
            showSizeChanger={false}
            disabled={loading}
            onChange={setPage}
          />
        )}
      </div>
    </Drawer>
  );
};

export default ConsentAuditLogDrawer;
